import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import useResponsive from 'src/hooks/useResponsive';
// services
import {
    getChatInfoByChatId,
    setSelectedMessage,
    clearNewMessagesClienteByChatId,
    setMessagesWhatsapp,
} from 'src/redux/slices/chat';
import { useDispatch, useSelector } from 'src/redux/store';
import { flushSync } from 'react-dom';
// components
import Scrollbar from 'src/components/Scrollbar';
import ChatComponent from 'src/components/_chats/chat/components/ChatPage/ChatComponent';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import SkeletonChatWindow from 'src/components/skeleton/chats/SkeletonChatWindow';
import { Box } from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import { groupMessagesByDate } from 'src/utils/chats.utils';
import ModalLightbox from 'src/@/components/shared/ModalLightbox';
import { removeMessageFromIDB, getImageBlob, removeImageBlob } from 'src/helpers/cache.helper';
import { useSnackbar } from 'notistack';
import { getAudioFromCache } from 'src/helpers/audio.helper';
import { toast } from 'src/@/components/ui/use-toast';
import ModalEncaminharMsg from './ChatComponent/ModalEncaminharMsg';
import ModalTratamentoIA from './ChatComponent/ModalTratamentoIA';
// actions
import v1_actionEnviarMenagemWhatsapp from 'src/actions/v1/whatsapp/v1_actionEnviarMenagemWhatsapp';
import v1_actionEnviarArquivoBuffer from 'src/actions/v1/whatsapp/v1_actionEnviarArquivoBuffer';
import { v2_actionEnviarAudioBuffer } from 'src/services/api/index';
import v2_actionDeletarNota from 'src/actions/v2/chats/v2_actionDeletarNota';
import v1_actionEncaminharMensagemWhatsapp from 'src/actions/v1/whatsapp/v1_actionEncaminharMensagemWhatsapp';
import v3_actionFeedbackAgent from 'src/actions/v3/ai/v3_actionFeedbackAgent';
import ChatAIAnalysisMessage from './ChatAIAnalysisMessage';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { verifyFeatures } from 'src/utils/functions';
import NewMessagesButton from 'src/components/_chats/NewMessagesButton';

// ----------------------------------------------------------------------

const spinAnimation = `
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;

ChatMessageList.propTypes = {
    conversa: PropTypes.array.isRequired,
    chat: PropTypes.object,
    cliente: PropTypes.object,
    callbackGpt: PropTypes.func,
    loadingChat: PropTypes.bool,
    config: PropTypes.object,
    source: PropTypes.string,
    onCallbackResend: PropTypes.func,
    mensagens: PropTypes.array,
    sentimentAnalysis: PropTypes.array,
};

const Chat = ({
    messages,
    config,
    chatInfo,
    isDesktop,
    onCallbakOpenModal,
    selectMessageId,
    onDeleteNote,
    callbackGpt,
    resendMsg,
    resendingId,
    onDeleteMsgAudioResend,
    onResendAudio,
    selectMessageResponder,
    renderAnalysis,
    highlightedMessageId,
    firstMessageRef,
}) => {
    const { t } = useTranslation();
    const [selectedMessages, setSelectedMessages] = useState([]);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [openEncaminharDialog, setOpenEncaminharDialog] = useState(false);
    const [loadingEncaminhar, setLoadingEncaminhar] = useState(false);
    const [openTratamentoIADialog, setOpenTratamentoIADialog] = useState(false);
    const [loadingTratamentoIA, setLoadingTratamentoIA] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { user, cliente } = useAuth();
    const MAX_MESSAGES = 8;

    const handleMessageSelect = (messageId) => {
        setIsSelectionMode(true);
        setSelectedMessages((prev) => {
            if (prev.includes(messageId)) {
                return prev.filter((id) => id !== messageId);
            }
            if (prev.length >= MAX_MESSAGES) {
                enqueueSnackbar(t('chat.messageList.maxMessagesReached', { count: MAX_MESSAGES }), {
                    variant: 'warning',
                    autoHideDuration: 3000,
                    anchorOrigin: { horizontal: 'center', vertical: 'top' },
                });
                return prev;
            }
            return [...prev, messageId];
        });
    };

    const handleEncaminharMensagens = async (phone) => {
        try {
            setLoadingEncaminhar(true);
            for (const messageId of selectedMessages) {
                const message = messages.find((msg) => msg.id === messageId);
                if (message) {
                    const data = { phone: phone, messageId: message.id };
                    const querys = `?sessao=${chatInfo.sessao}&canal=${chatInfo.canal}`;
                    await v1_actionEncaminharMensagemWhatsapp(querys, data);
                }
            }
            setLoadingEncaminhar(false);
            setOpenEncaminharDialog(false);
            setSelectedMessages([]);
            setIsSelectionMode(false);
            enqueueSnackbar(t('chat.messageList.forwardSuccess'), { variant: 'success' });
        } catch (error) {
            setLoadingEncaminhar(false);
            enqueueSnackbar(t('chat.messageList.forwardError'), { variant: 'error' });
            console.error(error);
        }
    };

    const handleEnviarParaIA = async (data) => {
        try {
            setLoadingTratamentoIA(true);
            const feedbackPayload = {
                user_id: user.id,
                cliente_id: chatInfo.cliente_id,
                chat_id: chatInfo._id,
                scope: {
                    messages: selectedMessages,
                    msg_wrong: data.respostaErrada,
                    msg_correct: data.respostaCorreta,
                },
            };
            await v3_actionFeedbackAgent(feedbackPayload);
            setLoadingTratamentoIA(false);
            setOpenTratamentoIADialog(false);
            setSelectedMessages([]);
            setIsSelectionMode(false);
            enqueueSnackbar(t('chat.messageList.aiFeedbackSuccess'), { variant: 'success' });
        } catch (error) {
            setLoadingTratamentoIA(false);
            enqueueSnackbar(t('chat.messageList.aiFeedbackError'), { variant: 'error' });
            console.error(error);
        }
    };

    const groupedMessages = groupMessagesByDate(messages);

    return (
        <div className="relative">
            <div>
                {Object.keys(groupedMessages).map((date) => (
                    <div key={date}>
                        <div className="w-full flex justify-center py-4">
                            <div className="px-3 py-1 bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-100 dark:border-gray-700 rounded-full text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 shadow-sm dark:shadow-gray-900/20">
                                {date}
                            </div>
                        </div>
                        {groupedMessages[date].map((message, index) => {
                            if (message.type === 'analysis') {
                                return (
                                    <div key={`analysis-${message.id || index}`} className="w-full my-4">
                                        {renderAnalysis && message.analysisData ? (
                                            renderAnalysis(message.analysisData)
                                        ) : (
                                            <div className="text-center text-gray-500 dark:text-gray-400 text-sm">{t('chat.messageList.analysisUnavailable')}</div>
                                        )}
                                    </div>
                                );
                            }
                            const isFirstMessage = index === 0;
                            const isFirstGroup = Object.keys(groupedMessages).indexOf(date) === 0;
                            return (
                                <div
                                    id={`message-${message.id || `temp-${index}`}`}
                                    key={message.id || index}
                                    ref={isFirstMessage && isFirstGroup ? firstMessageRef : null}
                                    className={`relative flex ${message.fromMe ? 'justify-end' : 'justify-start'
                                        } mt-1 transition-all duration-500 ${highlightedMessageId === message.id ? 'bg-[#7C3AED]/10 ring-1 ring-[#7C3AED]/20 rounded-2xl p-2' : ''
                                        }`}
                                >
                                    {isSelectionMode && message.type !== 'analysis' && (
                                        <div className="flex justify-center items-center mr-2">
                                            <Checkbox
                                                checked={selectedMessages.includes(message.id)}
                                                onCheckedChange={() => handleMessageSelect(message.id)}
                                            />
                                        </div>
                                    )}
                                    <div
                                        className={`flex flex-col ${message.type === 'analysis'
                                            ? 'items-center w-full'
                                            : `${message.fromMe ? 'items-end' : 'items-start'} w-full ${selectedMessages.includes(message.id) ? 'bg-[#7C3AED]/5 ring-1 ring-[#7C3AED]/20 rounded-2xl' : ''
                                            }`
                                            }`}
                                    >
                                        {chatInfo?.isGroup === true && message?.isGroupMessage && !message?.fromMe && message?.groupSenderName && (
                                            <div
                                                className="text-[11px] font-semibold mb-0.5 px-1"
                                                style={{ color: message.groupSenderColor || '#6366f1' }}
                                            >
                                                {message.groupSenderName}
                                            </div>
                                        )}
                                        <div className={`${isSelectionMode && message.type !== 'analysis' ? 'ml-8' : ''}`}>
                                            <ChatComponent
                                                onResendAudio={onResendAudio}
                                                onDeleteMsgAudioResend={onDeleteMsgAudioResend}
                                                config={config}
                                                chat={chatInfo}
                                                callbackGpt={(value) => {
                                                    callbackGpt(value);
                                                }}
                                                resendMsg={resendMsg}
                                                onDeleteNote={() => onDeleteNote(message.id)}
                                                isMe={message.fromMe ? 'right' : 'left'}
                                                message={message}
                                                onCallbakOpenModal={onCallbakOpenModal}
                                                selectMessageResponder={() => selectMessageResponder(message)}
                                                selectMessageId={(messageId) => handleMessageSelect(messageId)}
                                                isDesktop={isDesktop}
                                                loadingResend={resendingId === message.id}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Botão flutuante para encaminhar */}
            {selectedMessages.length > 0 && (
                <div className="fixed bottom-32 left-1/2 transform -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl shadow-xl shadow-gray-200/50 dark:shadow-gray-900/50 p-2 flex items-center gap-3 pr-4">
                        <div className="pl-3 pr-2 border-r border-gray-100 dark:border-gray-700">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                <span className="text-[#7C3AED] font-bold">{selectedMessages.length}</span>
                                <span className="text-gray-400 dark:text-gray-500 mx-1">/</span> {MAX_MESSAGES}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    setSelectedMessages([]);
                                    setIsSelectionMode(false);
                                }}
                                className="hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-2 rounded-xl transition-colors"
                                title={t('chat.messageList.clearSelection')}
                            >
                                <Iconify icon="material-symbols:close" />
                            </button>
                            <div className="h-4 w-px bg-gray-100 dark:bg-gray-700" />
                            <button
                                onClick={() => setOpenEncaminharDialog(true)}
                                disabled={loadingEncaminhar}
                                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-3 py-2 rounded-xl flex items-center gap-2 text-xs font-medium shadow-sm shadow-[#7C3AED]/20 transition-all hover:scale-105 active:scale-95"
                            >
                                {loadingEncaminhar ? <Iconify icon="svg-spinners:ring-resize" /> : <Iconify icon="material-symbols:forward" />}
                                <span>{t('chat.messageList.forward')}</span>
                            </button>
                            {verifyFeatures(cliente.features, 'ai.feedback') && (
                                <button
                                    onClick={() => setOpenTratamentoIADialog(true)}
                                    disabled={loadingTratamentoIA}
                                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-2 rounded-xl flex items-center gap-2 text-xs font-medium transition-all hover:scale-105 active:scale-95"
                                >
                                    {loadingTratamentoIA ? (
                                        <Iconify icon="svg-spinners:ring-resize" />
                                    ) : (
                                        <Iconify icon="material-symbols:psychology" className="text-[#7C3AED]" />
                                    )}
                                    <span>{t('chat.messageList.aiFeedback')}</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Encaminhar Mensagem */}
            {openEncaminharDialog && (
                <ModalEncaminharMsg
                    user={user}
                    open={openEncaminharDialog}
                    loading={loadingEncaminhar}
                    onSubmit={handleEncaminharMensagens}
                    onClose={() => setOpenEncaminharDialog(false)}
                />
            )}

            {/* Modal de Tratamento de IA */}
            {openTratamentoIADialog && (
                <ModalTratamentoIA
                    user={user}
                    open={openTratamentoIADialog}
                    loading={loadingTratamentoIA}
                    onSubmit={handleEnviarParaIA}
                    onClose={() => setOpenTratamentoIADialog(false)}
                />
            )}
        </div>
    );
};

export default function ChatMessageList({
    conversa,
    chat,
    cliente,
    callbackGpt,
    loadingChat,
    config,
    source,
    onCallbackResend,
    mensagens,
    sentimentAnalysis,
}) {
    const { t } = useTranslation();
    const scrollRef = useRef();
    const chatRef = useRef(null);
    const messagesEnd = useRef(null);
    const firstMessageRef = useRef(null);
    const dispatch = useDispatch();
    const isDesktop = useResponsive('up', 'md');
    const [setFiles] = useState([]);
    const { chatInfo, newMessagesCliente } = useSelector((state) => state.chat);
    const [clickedUrl, setClickedUrl] = useState('');
    const [clickedType, setClickedType] = useState('image');
    const [openModalImage, setOpenModalImage] = useState(false);
    // Id da mensagem em reenvio (antes eram dois booleanos globais: qualquer
    // reenvio deixava TODOS os balões de erro com spinner e botão desabilitado).
    const [resendingId, setResendingId] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const [highlightedMessageId, setHighlightedMessageId] = useState(null);
    const [isNavigatingToMessage, setIsNavigatingToMessage] = useState(false);
    const [lastMessageCount, setLastMessageCount] = useState(0);
    const [isAtBottom, setIsAtBottom] = useState(true);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const [allMessages, setAllMessages] = useState([]);
    const paginationRef = useRef({ currentPage: 1, hasNextPage: true, loading: false });
    const navigateToMessageRef = useRef(null);
    const navigationTokenRef = useRef(0);
    const isNavigatingRef = useRef(false);

    const emitNavigateStatus = (status, requestId = null) => {
        window.dispatchEvent(
            new CustomEvent('navigateToMessageStatus', { detail: { status, requestId } })
        );
    };

    useEffect(() => {
        paginationRef.current = { currentPage, hasNextPage, loading: loadingMoreMessages };
    }, [currentPage, hasNextPage, loadingMoreMessages]);

    function toTimestamp(date) {
        if (!date) return 0;
        try {
            return Math.floor(new Date(date).getTime() / 1000);
        } catch (error) {
            console.error('Erro ao converter data:', date, error);
            return 0;
        }
    }

    // Busca uma página mais antiga. preserveScroll=true mantém a posição visual
    // (botão "carregar anteriores"); false deixa o caller posicionar o scroll.
    const fetchOlderPage = async ({ preserveScroll = true } = {}) => {
        if (!chat?.sessao || !chat?.numeroCliente) {
            return { messages: [], hasNextPage: false, page: paginationRef.current.currentPage };
        }
        if (paginationRef.current.loading || !paginationRef.current.hasNextPage) {
            return {
                messages: [],
                hasNextPage: paginationRef.current.hasNextPage,
                page: paginationRef.current.currentPage,
            };
        }

        const scrollContainer = scrollRef.current;
        const previousScrollHeight = scrollContainer?.scrollHeight || 0;
        const previousScrollTop = scrollContainer?.scrollTop || 0;
        const nextPage = paginationRef.current.currentPage + 1;

        setLoadingMoreMessages(true);
        setIsAtBottom(false);
        paginationRef.current.loading = true;

        try {
            const querys = `?sessao=${chat.sessao}&numero=${chat.numeroCliente}&canal=${chat.canal || 'whatsapp'}&page=${nextPage}&limit=50&chat_source=db`;
            const { default: v2_actionBuscarMensagens } = await import('src/actions/v2/whatsapp/v2_actionBuscarMensagens');
            const response = await v2_actionBuscarMensagens(querys);

            if (response.data && response.data.status === 'success') {
                const { data: newMessages, pagination: newPagination } = response.data;
                if (newMessages && newMessages.length > 0) {
                    setAllMessages((prevMessages) => [...newMessages, ...prevMessages]);
                    setCurrentPage(nextPage);
                    setHasNextPage(!!newPagination.hasNextPage);
                    paginationRef.current = {
                        currentPage: nextPage,
                        hasNextPage: !!newPagination.hasNextPage,
                        loading: false,
                    };

                    if (preserveScroll) {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                if (scrollContainer) {
                                    const newScrollHeight = scrollContainer.scrollHeight;
                                    const heightDifference = newScrollHeight - previousScrollHeight;
                                    const newScrollTop = previousScrollTop + heightDifference;
                                    const maxScrollTop = newScrollHeight - scrollContainer.clientHeight;
                                    const finalScrollTop = Math.min(newScrollTop, maxScrollTop);
                                    scrollContainer.scrollTop = finalScrollTop;
                                    setIsAtBottom(finalScrollTop + scrollContainer.clientHeight >= newScrollHeight - 100);
                                }
                            });
                        });
                    }

                    return {
                        messages: newMessages,
                        hasNextPage: !!newPagination.hasNextPage,
                        page: nextPage,
                    };
                }
            }

            setHasNextPage(false);
            paginationRef.current = { ...paginationRef.current, hasNextPage: false, loading: false };
            return { messages: [], hasNextPage: false, page: paginationRef.current.currentPage };
        } catch (error) {
            console.error('Erro ao carregar mais mensagens:', error);
            enqueueSnackbar(t('chat.messageList.loadPreviousError'), {
                variant: 'error',
                autoHideDuration: 3000,
                anchorOrigin: { horizontal: 'center', vertical: 'top' },
            });
            paginationRef.current.loading = false;
            throw error;
        } finally {
            setLoadingMoreMessages(false);
            paginationRef.current.loading = false;
        }
    };

    const loadMoreMessages = async () => {
        if (loadingMoreMessages || !hasNextPage || !chat?.sessao || !chat?.numeroCliente) {
            return;
        }
        try {
            await fetchOlderPage({ preserveScroll: true });
        } catch (error) {
            // toast já emitido em fetchOlderPage
        }
    };

    useEffect(() => {
        if (chat?._id) {
            setAllMessages(conversa || []);
            setCurrentPage(1);
            setHasNextPage(true);
            setIsAtBottom(true);
            paginationRef.current = { currentPage: 1, hasNextPage: true, loading: false };
        }

    }, [chat?._id]);

    useEffect(() => {
        if (chat?._id) {
            navigationTokenRef.current += 1;
            isNavigatingRef.current = false;
            emitNavigateStatus('idle');
            setIsAtBottom(true);
            setIsNavigatingToMessage(false);
            setHighlightedMessageId(null);
            dispatch(clearNewMessagesClienteByChatId(chat._id));

            const forceScrollToBottom = () => {
                if (scrollRef.current) {
                    const scrollContainer = scrollRef.current;
                    scrollContainer.scrollTop = scrollContainer.scrollHeight;
                    setIsAtBottom(true);
                }
            };

            forceScrollToBottom();
            setTimeout(forceScrollToBottom, 50);
            setTimeout(forceScrollToBottom, 150);
            setTimeout(forceScrollToBottom, 300);
        }
    }, [chat?._id, dispatch]);

    const conversaArray = Array.isArray(conversa) ? [...conversa] : [];
    const menorTimestamp =
        conversaArray.length > 0
            ? Math.min(...conversaArray.map((m) => m.t || m.timestamp || toTimestamp(m.date) || 0))
            : 0;
    const maiorTimestamp =
        conversaArray.length > 0
            ? Math.max(...conversaArray.map((m) => m.t || m.timestamp || toTimestamp(m.date) || 0))
            : 0;

    const notesChat = (chatInfo?.notas || []).filter((nota) => {
        if (!nota || !nota.date) return false;
        const notaTimestamp = toTimestamp(nota.date);
        if (conversaArray.length === 0) return true;
        return notaTimestamp >= menorTimestamp;
    });

    const filteredSentimentAnalysis = (sentimentAnalysis || []).filter((analysis) => {
        if (!analysis) return false;
        const analysisTimestamp = analysis.last_analisys_timestamp
            ? parseInt(analysis.last_analisys_timestamp)
            : toTimestamp(analysis.date);
        if (conversaArray.length === 0) return true;
        const tolerance = 24 * 60 * 60;
        // A análise é datada de quando foi executada (agora), que pode ser
        // posterior à última mensagem do chat. Mantemos apenas o limite inferior
        // para descartar análises mais antigas que a janela carregada; análises
        // mais recentes que a última mensagem (ex.: feita hoje sem mensagem hoje)
        // devem sempre aparecer.
        return analysisTimestamp >= menorTimestamp - tolerance;
    });

    let messagesChat = [...conversaArray];
    if (allMessages.length > 0) {
        const conversaIds = new Set(conversaArray.map((m) => m.id));
        allMessages.forEach((msg) => {
            if (!conversaIds.has(msg.id)) {
                messagesChat.push(msg);
            }
        });
    }

    const seenIds = new Set();
    messagesChat = messagesChat.filter((m) => {
        const key = m.id || `${m.type}-${m.t || m.timestamp}`;
        if (seenIds.has(key)) return false;
        seenIds.add(key);
        return true;
    });

    notesChat.forEach((nota) => {
        const novaNota = {
            id: nota.id || `note-${Date.now()}-${Math.random()}`,
            body: nota.body,
            usuario_id: nota.usuario_id,
            date: nota.date,
            timestamp: toTimestamp(nota.date),
            t: toTimestamp(nota.date),
            type: 'note',
            fromMe: true,
            userName: nota.userName || nota.usuario_nome || t('chat.messageList.user'),
        };
        messagesChat.push(novaNota);
    });

    if (filteredSentimentAnalysis && Array.isArray(filteredSentimentAnalysis)) {
        filteredSentimentAnalysis.forEach((analysis) => {
            const timestamp = analysis.last_analisys_timestamp
                ? parseInt(analysis.last_analisys_timestamp)
                : Math.floor(new Date(analysis.date).getTime() / 1000);
            const analysisMessage = {
                id: `analysis-${analysis._id || Date.now()}`,
                type: 'analysis',
                timestamp: timestamp,
                t: timestamp,
                analysisData: analysis,
                fromMe: true,
            };
            messagesChat.push(analysisMessage);
        });
    }

    messagesChat = messagesChat.sort((a, b) => (a.t || 0) - (b.t || 0));

    const scrollMessagesToBottom = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            setIsAtBottom(true);
        }
    };

    const scrollMessagesToBottomSmooth = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
            setTimeout(() => setIsAtBottom(true), 150);
        }
    };

    const checkIfAtBottom = () => {
        if (scrollRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
            const threshold = 100;
            const isBottom = scrollTop + clientHeight >= scrollHeight - threshold;
            setIsAtBottom(isBottom);
            if (isBottom && chat?._id) {
                const countNewForChat = newMessagesCliente.filter((msg) => msg.chatId === chat._id).length;
                if (countNewForChat > 0) {
                    dispatch(clearNewMessagesClienteByChatId(chat._id));
                }
            }
        }
    };

    const handleScrollToBottom = () => {
        scrollMessagesToBottomSmooth();
        if (chat?._id) {
            dispatch(clearNewMessagesClienteByChatId(chat._id));
        }
        setIsAtBottom(true);
    };

    const scrollToMessageElement = (messageId) => {
        if (!messageId) return false;
        const messageElement = document.getElementById(`message-${messageId}`);
        const container = scrollRef.current;
        if (!messageElement || !container) return false;

        const elementRect = messageElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top;
        const scrollTop = container.scrollTop + relativeTop - containerRect.height / 2;
        container.scrollTo({ top: scrollTop, behavior: 'smooth' });
        return true;
    };

    const messageMatchesTarget = (msg, targetId, quotedHint) => {
        if (!msg?.id || msg.type === 'analysis' || msg.type === 'note') return false;

        if (targetId) {
            const target = String(targetId);
            const mid = String(msg.id);
            if (mid === target) return true;
            // WhatsApp: quotedMsgId às vezes é só o stanza; id completo termina com ele
            if (mid.endsWith(`_${target}`) || mid.endsWith(target)) return true;
            if (String(msg.telegram?.messageId || '') === target) return true;
        }

        if (quotedHint) {
            const hintBody = quotedHint.caption || quotedHint.body;
            if (!hintBody) return false;
            if (quotedHint.type && msg.type && quotedHint.type !== msg.type) return false;
            return (msg.caption || msg.body) === hintBody;
        }

        return false;
    };

    const findMessageInPool = (pool, targetId, quotedHint) => {
        if (!Array.isArray(pool)) return null;
        return pool.find((msg) => messageMatchesTarget(msg, targetId, quotedHint)) || null;
    };

    const waitForMessageInDom = async (messageId, attempts = 20, delayMs = 50) => {
        if (!messageId) return false;
        for (let i = 0; i < attempts; i += 1) {
            if (scrollToMessageElement(messageId)) return true;
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        return false;
    };

    const finishNavigationHighlight = (token, requestId = null) => {
        if (navigationTokenRef.current !== token) return;
        emitNavigateStatus('done', requestId);
        isNavigatingRef.current = false;
        setTimeout(() => {
            if (navigationTokenRef.current === token) {
                setHighlightedMessageId(null);
            }
        }, 3000);
        setTimeout(() => {
            if (navigationTokenRef.current === token) {
                setIsNavigatingToMessage(false);
            }
        }, 10000);
    };

    const failNavigation = (token, requestId = null) => {
        if (navigationTokenRef.current !== token) return;
        emitNavigateStatus('error', requestId);
        isNavigatingRef.current = false;
        setHighlightedMessageId(null);
        setIsNavigatingToMessage(false);
        enqueueSnackbar(t('chat.messageList.quotedMessageNotFound'), {
            variant: 'warning',
            autoHideDuration: 3000,
            anchorOrigin: { horizontal: 'center', vertical: 'top' },
        });
    };

    const mergeFetchedIntoList = (fetchedMessages) => {
        let mergedResult = [];
        // flushSync garante que o DOM já tem as mensagens antes do waitForMessageInDom
        flushSync(() => {
            setAllMessages((prev) => {
                const byId = new Map();
                const conversaArrayLocal = Array.isArray(conversa) ? conversa : [];
                [...(fetchedMessages || []), ...(prev || []), ...conversaArrayLocal].forEach((msg) => {
                    if (msg?.id) byId.set(String(msg.id), msg);
                });
                mergedResult = Array.from(byId.values()).sort((a, b) => (a.t || 0) - (b.t || 0));
                return mergedResult;
            });
        });
        if (mergedResult.length > 0) {
            dispatch(setMessagesWhatsapp(mergedResult));
        }
        return mergedResult;
    };

    const navigateToMessageById = async (messageId, quotedHint = null, requestId = null) => {
        if (!messageId && !quotedHint) {
            emitNavigateStatus('idle', requestId);
            return;
        }

        // Evita cliques repetidos enquanto a busca/scroll ainda está em andamento
        if (isNavigatingRef.current) {
            emitNavigateStatus('ignored', requestId);
            return;
        }

        const token = ++navigationTokenRef.current;
        isNavigatingRef.current = true;
        setIsNavigatingToMessage(true);
        setIsAtBottom(false);
        emitNavigateStatus('loading', requestId);

        const poolNow = [...(allMessages || []), ...(Array.isArray(conversa) ? conversa : [])];
        let found = findMessageInPool(poolNow, messageId, quotedHint);
        if (found?.id) {
            setHighlightedMessageId(found.id);
            if (await waitForMessageInDom(found.id)) {
                if (navigationTokenRef.current === token) finishNavigationHighlight(token, requestId);
                return;
            }
        } else if (messageId) {
            setHighlightedMessageId(messageId);
        }

        const NAV_LIMITS = [50, 150, 400, 1000];
        const PAGE_SIZE = 50;
        const canal = chat?.canal || 'whatsapp';

        try {
            if (!chat?.sessao || !chat?.numeroCliente) {
                throw new Error('chat incompleto');
            }

            const { default: v2_actionBuscarMensagens } = await import('src/actions/v2/whatsapp/v2_actionBuscarMensagens');

            // 1) Expande a janela recente (page=1 com limit crescente)
            for (const limit of NAV_LIMITS) {
                if (navigationTokenRef.current !== token) return;

                const querys = `?sessao=${chat.sessao}&numero=${chat.numeroCliente}&canal=${canal}&page=1&limit=${limit}&chat_source=db`;
                const response = await v2_actionBuscarMensagens(querys);
                if (navigationTokenRef.current !== token) return;

                if (!(response.data && response.data.status === 'success')) continue;

                const { data: fetchedMessages, pagination: newPagination } = response.data;
                const messages = Array.isArray(fetchedMessages) ? fetchedMessages : [];
                const merged = mergeFetchedIntoList(messages);

                const equivalentPage = Math.max(1, Math.ceil((messages.length || limit) / PAGE_SIZE));
                const nextHasPage = !!newPagination?.hasNextPage;
                setCurrentPage(equivalentPage);
                setHasNextPage(nextHasPage);
                paginationRef.current = {
                    currentPage: equivalentPage,
                    hasNextPage: nextHasPage,
                    loading: false,
                };

                found = findMessageInPool(merged, messageId, quotedHint);
                if (found?.id) {
                    setHighlightedMessageId(found.id);
                    if (await waitForMessageInDom(found.id, 30, 50)) {
                        if (navigationTokenRef.current === token) finishNavigationHighlight(token, requestId);
                        return;
                    }
                }

                // Só para se realmente não há mais histórico
                if (!nextHasPage) break;
            }

            // 2) Ainda não achou: pagina para trás até esgotar
            let pagesTried = 0;
            const MAX_EXTRA_PAGES = 30;
            while (
                navigationTokenRef.current === token &&
                paginationRef.current.hasNextPage &&
                pagesTried < MAX_EXTRA_PAGES
            ) {
                while (paginationRef.current.loading && navigationTokenRef.current === token) {
                    await new Promise((r) => setTimeout(r, 80));
                }
                if (navigationTokenRef.current !== token) return;

                let result;
                try {
                    result = await fetchOlderPage({ preserveScroll: false });
                } catch (error) {
                    break;
                }
                pagesTried += 1;

                if (!result?.messages?.length) break;

                const merged = mergeFetchedIntoList(result.messages);
                found = findMessageInPool(merged, messageId, quotedHint);

                if (found?.id) {
                    setHighlightedMessageId(found.id);
                    if (await waitForMessageInDom(found.id, 30, 50)) {
                        if (navigationTokenRef.current === token) finishNavigationHighlight(token, requestId);
                        return;
                    }
                }
            }
        } catch (error) {
            console.error('Erro ao carregar histórico para navegação:', error);
        }

        failNavigation(token, requestId);
    };

    navigateToMessageRef.current = navigateToMessageById;

    useEffect(() => {
        const currentMessagesCount = messagesChat.length;
        if (currentMessagesCount > lastMessageCount && !highlightedMessageId && !isNavigatingToMessage && !loadingMoreMessages) {
            const lastMessage = messagesChat[currentMessagesCount - 1];
            const isAgentMessage = lastMessage?.fromMe === true;
            if (isAgentMessage) {
                scrollMessagesToBottom();
                setIsAtBottom(true);
                if (chat?._id) {
                    dispatch(clearNewMessagesClienteByChatId(chat._id));
                }
            } else if (isAtBottom) {
                scrollMessagesToBottom();
                if (chat?._id) {
                    dispatch(clearNewMessagesClienteByChatId(chat._id));
                }
            }
            setLastMessageCount(currentMessagesCount);
        } else if (currentMessagesCount !== lastMessageCount) {
            setLastMessageCount(currentMessagesCount);
        }
    }, [
        messagesChat.length,
        highlightedMessageId,
        isNavigatingToMessage,
        lastMessageCount,
        isAtBottom,
        loadingMoreMessages,
        chat?._id,
        dispatch,
        messagesChat,
    ]);

    useEffect(() => {
        const handleNavigateToMessage = (event) => {
            const { messageId, quotedHint, requestId } = event.detail || {};
            navigateToMessageRef.current?.(messageId, quotedHint, requestId);
        };
        window.addEventListener('navigateToMessage', handleNavigateToMessage);
        return () => {
            window.removeEventListener('navigateToMessage', handleNavigateToMessage);
        };
    }, []);

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (scrollContainer) {
            const handleScroll = () => {
                checkIfAtBottom();
            };
            setTimeout(() => {
                checkIfAtBottom();
            }, 100);
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => {
                scrollContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [newMessagesCliente.length, chat?._id]);

    async function handleDeleteNote(id) {
        try {
            await v2_actionDeletarNota(id);
            dispatch(getChatInfoByChatId(chat._id, null, user.id, false));
        } catch (error) {
            console.error(error);
        }
    }

    async function resendMsg(msg) {
        if (msg.type === 'audio' || msg.type === 'ptt') {
            // Antes só buscava o cache e retornava — não reenviava nada. A UI roteia
            // áudio para onResendAudio, mas delegar aqui evita a falha silenciosa se
            // algum outro caminho chamar resendMsg com um áudio.
            await resendAudio(msg.id);
            return;
        }
        if (msg && (msg.type === 'image' || msg.type === 'video' || msg.type === 'document')) {
            try {
                setResendingId(msg.id);
                const blobUrl = await getImageBlob(msg.id);
                if (!blobUrl) {
                    enqueueSnackbar(t('chat.messageList.mediaNotInCache'), {
                        variant: 'warning',
                        autoHideDuration: 5000,
                        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
                    });
                    setResendingId(null);
                    return;
                }
                const response = await fetch(blobUrl);
                const blob = await response.blob();
                const formData = new FormData();
                const mime = msg.mimetype || blob.type || 'application/octet-stream';
                const ext = (mime.split('/')[1] || 'bin').split(';')[0];
                const filename = `${new Date().getTime()}.${ext}`;
                formData.append('filename', filename);
                formData.append('file', blob);
                // Instagram/Messenger exigem `scope` com o id do usuário na página Meta —
                // `phone` (usado pelos demais canais) é ignorado e o backend rejeita o
                // reenvio. Mesmo contrato do envio original (sendSingleFileBuffer em
                // ChatBottomBar.js).
                const isMetaChannel = chat.canal === 'instagram' || chat.canal === 'messenger';
                if (isMetaChannel) {
                    formData.append('scope', JSON.stringify({
                        messageType: 'response',
                        type: msg.type,
                        instagramUserId: chat.instagramUser?.id,
                        messengerUserId: chat.messengerUser?.id,
                    }));
                } else {
                    formData.append('phone', chat.numeroCliente);
                    if (msg.caption) formData.append('caption', msg.caption);
                }
                if (msg.type === 'document') {
                    formData.append('isDocument', true);
                }
                // canal real: com 'whatsapp' fixo, reenviar num chat de telegram ou
                // webchat caía no gateway do WhatsApp com uma chave de conversa que
                // não é telefone.
                const querys = `?sessao=${chat.sessao}&canal=${chat.canal}`;
                await v1_actionEnviarArquivoBuffer(querys, formData);
                if (isMetaChannel && msg.caption) {
                    // Legenda vai como mensagem de texto separada, igual ao envio original
                    // de arquivo em canais de página Meta.
                    await v1_actionEnviarMenagemWhatsapp(querys, {
                        userId: chat.canal === 'messenger' ? chat.messengerUser?.id : chat.instagramUser?.id,
                        message: msg.caption,
                    });
                }
                await removeMessageFromIDB('error_msg_send', msg.id);
                // O arquivo em cache não serve mais depois do reenvio bem sucedido.
                removeImageBlob(msg.id);
                URL.revokeObjectURL(blobUrl);
                onCallbackResend();
                setResendingId(null);
            } catch (error) {
                setResendingId(null);
                console.error('error', error);
                enqueueSnackbar(t('chat.messageList.resendMediaError'), {
                    variant: 'error',
                    autoHideDuration: 5000,
                    anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
                });
            }
            return;
        }
        if (!msg) return;
        try {
            setResendingId(msg.id);
            // Idem: o canal precisa ser o da conversa, não 'whatsapp' fixo.
            const querys = `?sessao=${chat.sessao}&canal=${chat.canal}`;
            // WhatsApp Business (Cloud API) não aceita o formato { phone, message } dos
            // demais canais — exige o payload `scope` no formato da Meta (mesmo contrato
            // do envio original em ChatBottomBar.js, branch whatsapp_business).
            let body;
            if (chat.canal === 'whatsapp_business') {
                const clientMessageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
                    ? crypto.randomUUID()
                    : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
                body = {
                    clientMessageId,
                    scope: {
                        messaging_product: 'whatsapp',
                        recipient_type: 'individual',
                        to: chat.numeroCliente,
                        type: 'text',
                        text: { body: msg.body },
                    },
                };
            } else {
                body = { phone: chat.numeroCliente, message: msg.body };
            }
            await v1_actionEnviarMenagemWhatsapp(querys, body);
            await removeMessageFromIDB('error_msg_send', msg.id);
            onCallbackResend();
            setResendingId(null);
        } catch (error) {
            setResendingId(null);
            console.error('error', error);
            enqueueSnackbar(t('chat.errors.sendFailed'), {
                variant: 'error',
                autoHideDuration: 5000,
                anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
            });
        }
    }

    function deleteMsgAudioResend(msg) {
        removeMessageFromIDB('error_msg_send', msg.id);
        // O áudio é gravado por initCacheFileSave em image-store/blobs; o banco
        // 'error_send_audio' não existe — a chamada antiga só criava um banco vazio
        // e deixava o blob órfão.
        removeImageBlob(msg.id);
        onCallbackResend();
    }

    const resendAudio = async (messageId) => {
        let audioUrl;
        try {
            setResendingId(messageId);
            audioUrl = await getAudioFromCache(messageId);
            if (!audioUrl) {
                throw new Error(t('chat.messageList.audioNotInCache'));
            }
            const response = await fetch(audioUrl);
            const audioBlob = await response.blob();
            const formData = new FormData();
            // A extensão precisa bater com o mimetype real do blob (o cache grava
            // audio/mp3); com '.ogg' fixo o backend recebia um mp3 rotulado de ogg.
            const mime = audioBlob.type || 'audio/mp3';
            const ext = (mime.split('/')[1] || 'mp3').split(';')[0];
            formData.append('filename', `${new Date().getTime()}.${ext}`);
            formData.append('file', audioBlob);
            formData.append('phone', chat.numeroCliente);
            // Sem isVoice o áudio chegava como arquivo anexado, e não como mensagem
            // de voz — o envio original manda esse campo.
            formData.append('isVoice', 'true');
            const querys = `?sessao=${chat.sessao}&canal=${chat.canal}`;
            // Mesmo roteamento do envio original: o canal oficial usa o endpoint de
            // arquivo, o não-oficial o de áudio.
            if (chat.canal === 'whatsapp_business') {
                await v1_actionEnviarArquivoBuffer(querys, formData);
            } else {
                await v2_actionEnviarAudioBuffer(querys, formData);
            }
            await removeMessageFromIDB('error_msg_send', messageId);
            removeImageBlob(messageId);
            // Sem isto o balão de erro continuava na tela depois do reenvio bem
            // sucedido, e cada novo clique mandava outra cópia do áudio ao cliente.
            onCallbackResend();
            setResendingId(null);
            toast({ description: t('chat.messageList.resendAudioSuccess') });
        } catch (err) {
            setResendingId(null);
            console.error('Erro ao reenviar áudio:', err);
            toast({ variant: 'destructive', description: t('chat.messageList.resendAudioError') });
        } finally {
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        }
    };

    const { getInputProps } = useDropzone({
        accept: ['image/*', 'application/*'],
        onDrop: (acceptedFiles) => {
            setFiles(acceptedFiles.map((file) => Object.assign(file, { preview: URL.createObjectURL(file) })));
        },
    });

    const currentChatNewMessages = newMessagesCliente.filter((msg) => msg.chatId === chat?._id);
    const newMessagesCount = currentChatNewMessages.length;

    return (
        <>
            <style>{spinAnimation}</style>
            <Scrollbar scrollableNodeProps={{ ref: scrollRef }} sx={{ p: 3, pb: 20 }}>
                <input {...getInputProps()} />
                <ModalLightbox open={openModalImage} onClose={() => setOpenModalImage(false)} src={clickedUrl} type={clickedType} />
                <ul ref={chatRef}>
                    <div className="flex justify-center items-center py-4">
                        {hasNextPage ? (
                            <Button
                                onClick={loadMoreMessages}
                                disabled={loadingMoreMessages}
                                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full px-6 py-2 text-[12px] font-semibold shadow-lg shadow-[#7C3AED]/25 hover:shadow-xl hover:shadow-[#7C3AED]/30 transition-all duration-300 hover:-translate-y-0.5 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:shadow-none"
                            >
                                {loadingMoreMessages ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        <span className="ml-2">{t('chat.messageList.loadingMessages')}</span>
                                    </>
                                ) : (
                                    <>
                                        <Iconify icon="mdi:arrow-up" className="w-4 h-4" />
                                        <span className="ml-2">{t('chat.messageList.loadPrevious')}</span>
                                    </>
                                )}
                            </Button>
                        ) : allMessages.length > 0 ? (
                            <div className="flex items-center gap-2 px-5 py-3 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                                <Iconify icon="mdi:check-circle" className="w-4 h-4 text-green-500 dark:text-green-400" />
                                <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">{t('chat.messageList.allMessagesLoaded')}</span>
                            </div>
                        ) : null}
                    </div>

                    {loadingChat ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <SkeletonChatWindow />
                        </Box>
                    ) : (
                        <Chat
                            onDeleteMsgAudioResend={deleteMsgAudioResend}
                            onResendAudio={resendAudio}
                            messages={messagesChat}
                            resendMsg={resendMsg}
                            resendingId={resendingId}
                            chatInfo={chatInfo}
                            config={config}
                            isDesktop={isDesktop}
                            callbackGpt={callbackGpt}
                            onDeleteNote={(value) => handleDeleteNote(value)}
                            onCallbakOpenModal={(url, type = 'image') => {
                                setClickedUrl(url);
                                setClickedType(type);
                                setOpenModalImage(true);
                            }}
                            selectMessageResponder={(message) => {
                                if (!isNavigatingToMessage) {
                                    scrollMessagesToBottom();
                                }
                                dispatch(setSelectedMessage({ message: message }));
                            }}
                            highlightedMessageId={highlightedMessageId}
                            firstMessageRef={firstMessageRef}
                            renderAnalysis={(analysisData) => {
                                if (!analysisData) return null;
                                return <ChatAIAnalysisMessage data={analysisData} user={user} />;
                            }}
                        />
                    )}
                    <div ref={messagesEnd} />
                </ul>
            </Scrollbar>
            <NewMessagesButton
                newMessagesCount={newMessagesCount}
                onScrollToBottom={handleScrollToBottom}
                isVisible={!isAtBottom && newMessagesCount > 0}
            />
        </>
    );
}